import {
  KPI_WIDGETS_LIMIT,
  QUICKSTATS_WIDGETS_LIMIT,
  SEARCHBAR_FILTERS_LIMIT,
  TABLE_COLUMNS_LIMIT,
} from 'consts/common';
import {
  TEMPLATE_MAPPER,
} from 'consts/templating';
import { TEMPLATING_TRANSLATION_NAMESPACE } from 'consts/translation';
import ApiError from 'errors/apiError';
import NotAuthorizedError from 'errors/notAuthorizedError';
import { t } from 'i18n';

import { deepCopy, isItEmpty } from './manipulators';

export const appName = 'energycom';

export const defaultQueryProps = {
  refetchOnWindowFocus: false,
  useErrorBoundary: (err) => (
    err instanceof NotAuthorizedError
      || (
        err instanceof ApiError
        && !err.notify
      )
  ),
  // its better not to use this for ram issues. use this when bug gets opened
  // keepPreviousData: true,
};

export const allItemTemplate = {
  id: -1,
  isDefault: false,
  isFavorite: true,
  name: 'All',
  pageReference: 'default-all-templating',
  templateUrl: 'default-all-templating',
  webappName: appName,
};

export const TEMPLATE_PROPERTIES_INIT_STATE = {
  id: allItemTemplate.id,
  label: allItemTemplate.name,
  isFavorite: allItemTemplate.isFavorite,
  isDefault: allItemTemplate.isDefault,
};

export const DEFAULT_MAX_VISIBLE_LIMIT = 10;

export const DEFAULT_DRAFT = () => ({
  id: 'draftToAdd',
  label: t(`${TEMPLATING_TRANSLATION_NAMESPACE}:newTemplate`),
  favorite: true,
  selected: true,
  default: false,
});

export const handleExternalFavTemplate = async ({
  getTemplateCb,
  history,
  templateIDFromHeader,
  templateList,
  updateTemplateCb,
}) => {
  if (!templateIDFromHeader || isItEmpty(templateList)) return;

  await getTemplateCb(templateIDFromHeader);
  updateTemplateCb(templateIDFromHeader);
  history.replace({ ...history.location, state: undefined });
};

const genericFilter = (obj, filtersIds) => {
  if (obj === undefined || obj === null || filtersIds === undefined) return undefined;

  const res = filtersIds.indexOf(obj.id) !== -1;

  return res ? obj : undefined;
};

export const filterInput = (input, filtersIds) => {
  if (input === undefined
    || input === null
    || filtersIds === undefined
    || (!filtersIds.find((id) => id === input.id))) return undefined;

  const toRet = deepCopy(input);

  const filteredOptions = input
    ?.options
    ?.filter((el) => filtersIds.indexOf(el.id) !== -1);

  toRet.options = filteredOptions;

  return toRet;
};

export const settingSorter = (a, b) => {
  if (a.label < b.label) return -1;
  if (a.label > b.label) return 1;
  return 0;
};

export const filterSettingSorter = (a, b) => {
  if (a.id === 'multiSearch') return -1;
  if (b.id === 'multiSearch') return 1;
  if (a.isSubElement && !b.isSubElement) return -1;
  if (b.isSubElement && !a.isSubElement) return 1;
  return settingSorter(a, b);
};

export const getAllAvailableProps = (arr, dependencyMap) => arr.filter((item) => {
  if (!item?.deps) return dependencyMap.indexOf(item.id) !== -1; // se non sono state specificare deps, considera la prop come available
  if (item.id === 'multiSearch') {
    return item?.deps?.some((dependency) => dependencyMap.indexOf(dependency) !== -1);
  }
  return item?.deps?.every((dependency) => dependencyMap.indexOf(dependency) !== -1);
});

export const getVisibleProps = (arr, dependencyMap) => getAllAvailableProps(arr, dependencyMap)
  .filter((item) => item?.isVisible);

const composeObject = (
  obj, propString, acc,
) => {
  const dotPosition = propString.indexOf('.');
  if (dotPosition !== -1) {
    const currentProp = propString.substring(0, dotPosition);
    const newObj = obj[currentProp];
    if (newObj) {
      const restOfProp = propString.substring(dotPosition + 1);
      if (!acc[currentProp]) acc[currentProp] = {};
      composeObject(
        newObj, restOfProp, acc[currentProp],
      );
    }
  } else {
    acc[propString] = obj[propString];
  }
};

export const entityMap = (list, conf) => list.map((entry) => {
  const toRet = {};

  conf.forEach((prop) => {
    if (!prop?.deps) { // se non sono specificato nessuna deps usa l'id come deps di default
      toRet[prop?.id] = entry[prop?.id];
    } else {
      prop?.deps.forEach((dep) => { // cicla per ogni deps
        if (dep.includes('.')) {
          if (!toRet[prop.id]) toRet[prop.id] = {};
          composeObject(
            entry, dep, toRet[prop.id],
          );
        } else if (prop?.deps.length === 1) {
          toRet[prop?.id] = entry[dep];
        } else {
          toRet[prop?.id] = { ...toRet[prop?.id], [dep]: entry[dep] };
        }
      });
    }
  });

  return { ...toRet, dto: entry };
});

export const generateTableSideMenu = (
  availableColumns, templateColumns, maxLimit,
) => {
  const defaultToggles = [];
  const allToggles = [];

  availableColumns.forEach((el) => {
    const relativeElement = templateColumns.find((col) => col.id === el.id);

    const arr = relativeElement.isDefault ? defaultToggles : allToggles;

    if (relativeElement) {
      if (el?.id && el?.label) {
        arr.push({
          id: el.id,
          label: el.label,
          isVisible: relativeElement.isVisible,
        });
      }
    }
  });

  defaultToggles.sort(filterSettingSorter);
  allToggles.sort(filterSettingSorter);

  return [
    {
      title: `${t('components.defaultToggles')} ${t('components.table')} max(${maxLimit})`,
      toggles: defaultToggles,
    },
    {
      title: `${t('components.allToggles')} ${t('components.table')}`,
      toggles: allToggles,
    },
  ];
};

export const generateSearchbarSideMenu = (
  availableFilters, templateFilters, maxLimit,
) => {
  const defaultToggles = [];
  const allToggles = [];

  availableFilters.forEach((el) => {
    const relativeElement = templateFilters.find((filter) => filter.id === el.id);

    const arr = relativeElement.isDefault ? defaultToggles : allToggles;

    if (relativeElement) {
      arr.push({
        label: el.label ?? t('components.search'),
        id: el.id,
        isVisible: relativeElement.isVisible,
        hasDefaultValue: !!el.defaultValue,
      });
      if (el.type === 'input') {
        el.options.forEach((opt) => {
          const relativeSubElement = templateFilters.find((filter) => filter.id === opt.id);
          arr.push({
            label: opt.label,
            id: opt.id,
            isSubElement: true,
            parentId: el.id,
            isVisible: relativeSubElement.isVisible,
            hasDefaultValue: !!el.defaultValue,
          });
        });
      }
    }
  });

  defaultToggles.sort(filterSettingSorter);
  allToggles.sort(filterSettingSorter);

  return [
    {
      title: `${t('components.defaultToggles')} ${t('components.searchbar')} max(${maxLimit})`,
      toggles: defaultToggles,
    },
    {
      title: `${t('components.allToggles')} ${t('components.searchbar')}`,
      toggles: allToggles,
    },
  ];
};

export const generateWidgetsSideMenu = (
  availableWidgets, templateWidgets, maxLimit,
) => {
  const defaultToggles = [];
  const allToggles = [];

  availableWidgets.forEach((el) => {
    const relativeElement = templateWidgets.find((widget) => widget.id === el.id);

    const arr = relativeElement.isDefault ? defaultToggles : allToggles;

    if (relativeElement) {
      if (el?.id && el?.title) {
        arr.push({
          id: el.id,
          label: el.title,
          isVisible: relativeElement.isVisible,
        });
      }
    }
  });

  defaultToggles.sort(filterSettingSorter);
  allToggles.sort(filterSettingSorter);

  return [
    {
      title: `${t('components.defaultToggles')} ${t('components.quickStats')} max(${maxLimit})`,
      toggles: defaultToggles,
    },
    {
      title: `${t('components.allToggles')} ${t('components.quickStats')}`,
      toggles: allToggles,
    },
  ];
};

export const filterSearchBarFiltersByIds = (searchBarFilters, visibleSearchBarFiltersIds) => searchBarFilters.map((filter) => {
  switch (filter.type) {
  case 'input':
    return filterInput(filter, visibleSearchBarFiltersIds);
  case 'date':
  case 'select':
  case 'multiSelect':
  case 'singleInput':
  case 'toggle':
  case 'singleDate':
  case 'autocomplete':
    return genericFilter(filter, visibleSearchBarFiltersIds);
  default: return undefined;
  }
}).filter(Boolean);

export const genericConf = (pathLocation) => {
  if (!pathLocation) {
    throw new Error('Pathlocation is needed');
  }
  const currentConf = TEMPLATE_MAPPER[pathLocation]?.conf;
  if (!currentConf) {
    throw new Error(`Conf not mapped on this pathlocation: ${pathLocation}`);
  }
  const {
    filters = [],
    columns = [],
    widgets = [],
    kpis = [],
  } = currentConf;

  return {
    searchbar: {
      maxVisibleLimit: SEARCHBAR_FILTERS_LIMIT,
      filters,
      filtersValue: [],
    },
    table: {
      maxVisibleLimit: TABLE_COLUMNS_LIMIT,
      columns,
    },
    quickStats: {
      maxVisibleLimit: QUICKSTATS_WIDGETS_LIMIT,
      widgets,
    },
    kpis: {
      maxVisibleLimit: KPI_WIDGETS_LIMIT,
      kpis,
    },
  };
};

export default genericConf;
